import { lazy, type ComponentType, type LazyExoticComponent } from "react";
import type { TopNavId } from "../layouts/navigation";
import type { Role } from "../auth/roles";
import { INVEST_TOOLS } from "./invest/content";

/// Optional sidebar group. Panels sharing the same `group.id` collapse
/// under a single SubMenu (`group.title` as the SubMenu label). The
/// router stays flat — URLs are still `/<topNav>/<panelId>` —
/// grouping is purely a sidebar-presentation concern.
export interface PanelGroup {
  id: string;
  title: string;
}

export interface PanelDef {
  id: string;
  topNav: TopNavId;
  title: string;
  /// Required for in-app panels; omitted for external-link entries.
  component?: LazyExoticComponent<ComponentType>;
  group?: PanelGroup;
  /// When set, the sidebar item opens this URL in a new tab instead of
  /// routing to an internal panel. `component` should be omitted.
  externalUrl?: string;
  /// Panel depends on the `/api/feed` + `/ws` stack — hidden from roles
  /// whose `usesWebSocket` is false (i.e. `user`).
  requiresWs?: boolean;
  /// When set, only these roles see this panel (per-panel gate, finer than the
  /// per-topNav `canSee`). Used to keep the Quant vs Maker groups isolated
  /// under the shared `trader` tab. Omitted = visible to every role that can
  /// see the tab.
  roles?: Role[];
}

/// The two first-level groups under the merged `trader` tab; `id` must not
/// collide with any panel id. Quant is listed first so its group renders on top.
const QUANT_GROUP: PanelGroup = { id: "quant", title: "Quant" };
const MAKER_GROUP: PanelGroup = { id: "maker", title: "Maker" };

const ORDERBOOK_GROUP: PanelGroup = {
  id: "orderbook-group",
  title: "实时盘口",
};

const MACRO_GROUP: PanelGroup = {
  id: "macro-liquidity",
  title: "全球股市",
};
const BONDS_GROUP: PanelGroup = { id: "bonds", title: "全球债市" };
const FOREX_GROUP: PanelGroup = { id: "forex-market", title: "外汇市场" };
const DOLLAR_GROUP: PanelGroup = { id: "dollar", title: "美元指标" };

const CRYPTO_GROUP: PanelGroup = { id: "crypto-data", title: "Crypto宏观指标" };

const BTC_GROUP: PanelGroup = { id: "btc-data", title: "BTC宏观指标" };

const Placeholder = lazy(() => import("./Placeholder"));

// 全球投资 — 十大投资工具(《如何配置全球资产》框架),每个工具一个
// 侧栏分组,子项:底层逻辑 + 其他(占位)。
const INVEST_LOGIC: Record<string, LazyExoticComponent<ComponentType>> = {
  cash: lazy(() => import("./invest").then((m) => ({ default: m.CashLogic }))),
  property: lazy(() => import("./invest").then((m) => ({ default: m.PropertyLogic }))),
  bonds: lazy(() => import("./invest").then((m) => ({ default: m.BondsLogic }))),
  equities: lazy(() => import("./invest").then((m) => ({ default: m.EquitiesLogic }))),
  commodities: lazy(() => import("./invest").then((m) => ({ default: m.CommoditiesLogic }))),
  funds: lazy(() => import("./invest").then((m) => ({ default: m.FundsLogic }))),
  insurance: lazy(() => import("./invest").then((m) => ({ default: m.InsuranceLogic }))),
  forex: lazy(() => import("./invest").then((m) => ({ default: m.ForexLogic }))),
  derivatives: lazy(() => import("./invest").then((m) => ({ default: m.DerivativesLogic }))),
  crypto: lazy(() => import("./invest").then((m) => ({ default: m.CryptoLogic }))),
};

// 「标的观察」— 商品/基金/外汇/加密资产 四类接入 Yahoo 日线行情表;其余工具
// 第二个子项仍是「其他」占位。
const INVEST_WATCHLIST: Record<string, LazyExoticComponent<ComponentType>> = {
  commodities: lazy(() => import("./invest").then((m) => ({ default: m.CommoditiesWatch }))),
  funds: lazy(() => import("./invest").then((m) => ({ default: m.FundsWatch }))),
  forex: lazy(() => import("./invest").then((m) => ({ default: m.ForexWatch }))),
  crypto: lazy(() => import("./invest").then((m) => ({ default: m.CryptoWatch }))),
};

// 侧栏顺序:带「标的观察」的四类(商品/外汇/基金/加密资产)置顶,其余工具
// 保持 content.ts 的原始顺序。
const WATCHLIST_FIRST = ["commodities", "forex", "funds", "crypto"];
const ORDERED_INVEST_TOOLS = [
  ...WATCHLIST_FIRST.map((id) => INVEST_TOOLS.find((t) => t.id === id)).filter(
    (t): t is (typeof INVEST_TOOLS)[number] => t !== undefined,
  ),
  ...INVEST_TOOLS.filter((t) => !WATCHLIST_FIRST.includes(t.id)),
];

const INVEST_PANELS: PanelDef[] = ORDERED_INVEST_TOOLS.flatMap((tool) => {
  // 侧栏组标题去掉「1. 」式数字序号(排序由 ORDERED_INVEST_TOOLS 保证,
  // 序号在侧栏里是噪音;详情页 Card 标题仍用完整 title)。
  const group: PanelGroup = { id: `invest-${tool.id}`, title: tool.title.replace(/^\d+\.\s*/, '') };
  const watchlist = INVEST_WATCHLIST[tool.id];
  const second: PanelDef = watchlist
    ? {
        id: `${tool.id}-watch`,
        topNav: "invest" as TopNavId,
        title: "标的观察",
        component: watchlist,
        group,
      }
    : {
        id: `${tool.id}-other`,
        topNav: "invest" as TopNavId,
        title: "其他",
        component: Placeholder,
        group,
      };
  return [
    {
      id: `${tool.id}-logic`,
      topNav: "invest" as TopNavId,
      title: "底层逻辑",
      component: INVEST_LOGIC[tool.id],
      group,
    },
    second,
  ];
});
const RadarDashboard = lazy(() => import("./radar/RadarDashboard"));
const FractalDashboard = lazy(() => import("./fractal/FractalDashboard"));
const SystemInfo = lazy(() => import("./SystemInfo"));
const GridLive = lazy(() => import("./quant/GridLive"));
const GridBacktest = lazy(() => import("./quant/GridBacktest"));
const OrderbookBinance = lazy(() => import("./Orderbook"));
const OrderbookBinancePerp = lazy(() => import("./OrderbookBinancePerp"));
const OrderbookHyperliquid = lazy(() => import("./OrderbookHyperliquid"));
const OrderbookBybitSpot = lazy(() => import("./OrderbookBybitSpot"));
const OrderbookBybitPerp = lazy(() => import("./OrderbookBybitPerp"));
const OrderbookBitgetSpot = lazy(() => import("./OrderbookBitgetSpot"));
const OrderbookBitgetPerp = lazy(() => import("./OrderbookBitgetPerp"));
const OrderbookGateioSpot = lazy(() => import("./OrderbookGateioSpot"));
const OrderbookGateioPerp = lazy(() => import("./OrderbookGateioPerp"));
const OrderbookOkxSpot = lazy(() => import("./OrderbookOkxSpot"));
const OrderbookOkxSwap = lazy(() => import("./OrderbookOkxSwap"));
const Stablecoins = lazy(() => import("./signals/Stablecoins"));
const FundingRates = lazy(() => import("./signals/FundingRates"));
const BtcSpotPrices = lazy(() => import("./signals/BtcSpotPrices"));
const FearGreed = lazy(() => import("./signals/FearGreedPanel"));
const BtcPowerLaw = lazy(() => import("./signals/powerlaw/BtcPowerLawPanel"));
const BtcPowerTrend = lazy(() => import("./signals/powertrend/BtcPowerTrendPanel"));
const UsEquities = lazy(() => import("./signals/UsEquities"));
const MarketOverview = lazy(() => import("./signals/MarketOverview"));
const MostActive = lazy(() => import("./signals/MostActive"));
const GlobalOverview = lazy(() => import("./signals/GlobalOverview"));
const Manager13F = lazy(() => import("./signals/Manager13F"));
const ForexMarket = lazy(() => import("./signals/ForexMarket"));

// 美元 二级菜单 — regime 综合面板 + 12 个流动性指标
const DollarRegimePanel = lazy(() =>
  import("./signals/dollar").then((m) => ({ default: m.DollarRegimePanel })),
);
const DollarNetLiquidity = lazy(() =>
  import("./signals/dollar").then((m) => ({ default: m.DollarNetLiquidity })),
);
const DollarFedBalanceSheet = lazy(() =>
  import("./signals/dollar").then((m) => ({ default: m.DollarFedBalanceSheet })),
);
const DollarTga = lazy(() =>
  import("./signals/dollar").then((m) => ({ default: m.DollarTga })),
);
const DollarOnrrp = lazy(() =>
  import("./signals/dollar").then((m) => ({ default: m.DollarOnrrp })),
);
const DollarM2 = lazy(() =>
  import("./signals/dollar").then((m) => ({ default: m.DollarM2 })),
);
const DollarDxy = lazy(() =>
  import("./signals/dollar").then((m) => ({ default: m.DollarDxy })),
);
const DollarRealYield10y = lazy(() =>
  import("./signals/dollar").then((m) => ({ default: m.DollarRealYield10y })),
);
const DollarVix = lazy(() =>
  import("./signals/dollar").then((m) => ({ default: m.DollarVix })),
);
const DollarHySpread = lazy(() =>
  import("./signals/dollar").then((m) => ({ default: m.DollarHySpread })),
);
const DollarSofrIorb = lazy(() =>
  import("./signals/dollar").then((m) => ({ default: m.DollarSofrIorb })),
);
const DollarSrf = lazy(() =>
  import("./signals/dollar").then((m) => ({ default: m.DollarSrf })),
);
const DollarBankCashBuffer = lazy(() =>
  import("./signals/dollar").then((m) => ({ default: m.DollarBankCashBuffer })),
);

const PANELS: PanelDef[] = [
  // Home — order matters twice over: it is the sidebar order, and the first
  // entry is what `/home` redirects to (see the index route in App.tsx).
  // `system-info` leads because it is the only one backed by a real
  // component; the rest are still placeholders.
  {
    id: "system-info",
    topNav: "home",
    title: "系统信息",
    component: SystemInfo,
  },
  { id: "summary", topNav: "home", title: "全局汇总", component: Placeholder },
  { id: "pnl", topNav: "home", title: "总收益", component: Placeholder },
  {
    id: "connectivity",
    topNav: "home",
    title: "连接状态",
    component: Placeholder,
  },
  { id: "alerts", topNav: "home", title: "告警与监控", component: Placeholder },

  // Trader — Quant + Maker 合并为一个 tab,左侧两个一级组(Quant 在上)。
  // 按角色隔离:quant 角色只见 Quant 组,maker 角色只见 Maker 组,admin 两组都见
  // (前端 roles 过滤 + 后端 /api/grid 硬门禁 admin+quant 双保险)。
  {
    id: "grid-live",
    topNav: "trader",
    title: "网格实盘",
    component: GridLive,
    group: QUANT_GROUP,
    roles: ["admin", "quant"],
  },
  {
    id: "grid-backtest",
    topNav: "trader",
    title: "网格回测",
    component: GridBacktest,
    group: QUANT_GROUP,
    roles: ["admin", "quant"],
  },
  {
    id: "overview",
    topNav: "trader",
    title: "做市概览",
    component: Placeholder,
    group: MAKER_GROUP,
    roles: ["admin", "maker"],
  },
  {
    id: "instances",
    topNav: "trader",
    title: "做市实例",
    component: Placeholder,
    group: MAKER_GROUP,
    roles: ["admin", "maker"],
  },
  {
    id: "risk",
    topNav: "trader",
    title: "做市风控",
    component: Placeholder,
    group: MAKER_GROUP,
    roles: ["admin", "maker"],
  },

  // Signals
  {
    id: "market-overview",
    topNav: "signals",
    title: "美股.市场明细",
    component: MarketOverview,
    group: MACRO_GROUP,
  },
  {
    id: "global-overview",
    topNav: "signals",
    title: "全球股市温度",
    component: GlobalOverview,
    group: MACRO_GROUP,
  },
  {
    id: "most-active",
    topNav: "signals",
    title: "美股.流动性前50",
    component: MostActive,
    group: MACRO_GROUP,
  },
  {
    id: "manager-13f",
    topNav: "signals",
    title: "全球顶级基金",
    component: Manager13F,
    group: MACRO_GROUP,
  },
  {
    id: "us-equities",
    topNav: "signals",
    title: "美股.七巨头",
    component: UsEquities,
    group: MACRO_GROUP,
  },

  {
    id: "global-bonds",
    topNav: "signals",
    title: "全球债市",
    component: Placeholder,
    group: BONDS_GROUP,
  },

  {
    id: "global-forex",
    topNav: "signals",
    title: "外汇详情",
    component: ForexMarket,
    group: FOREX_GROUP,
  },

  // 美元 二级菜单 — 综合 regime 面板 + 12 个 dollarliquidity 流动性指标
  {
    id: "dollar-regime",
    topNav: "signals",
    title: "流动性评分",
    component: DollarRegimePanel,
    group: DOLLAR_GROUP,
  },
  {
    id: "dollar-net-liquidity",
    topNav: "signals",
    title: "净流动性",
    component: DollarNetLiquidity,
    group: DOLLAR_GROUP,
  },
  {
    id: "dollar-fed-balance-sheet",
    topNav: "signals",
    title: "美联储资产负债表",
    component: DollarFedBalanceSheet,
    group: DOLLAR_GROUP,
  },
  {
    id: "dollar-tga",
    topNav: "signals",
    title: "TGA 财政部账户",
    component: DollarTga,
    group: DOLLAR_GROUP,
  },
  {
    id: "dollar-onrrp",
    topNav: "signals",
    title: "隔夜逆回购 ON RRP",
    component: DollarOnrrp,
    group: DOLLAR_GROUP,
  },
  {
    id: "dollar-m2",
    topNav: "signals",
    title: "M2 货币供应",
    component: DollarM2,
    group: DOLLAR_GROUP,
  },
  {
    id: "dollar-dxy",
    topNav: "signals",
    title: "美元指数 DXY",
    component: DollarDxy,
    group: DOLLAR_GROUP,
  },
  {
    id: "dollar-real-yield-10y",
    topNav: "signals",
    title: "10Y 实际收益率",
    component: DollarRealYield10y,
    group: DOLLAR_GROUP,
  },
  {
    id: "dollar-vix",
    topNav: "signals",
    title: "VIX 波动率指数",
    component: DollarVix,
    group: DOLLAR_GROUP,
  },
  {
    id: "dollar-hy-spread",
    topNav: "signals",
    title: "高收益债利差",
    component: DollarHySpread,
    group: DOLLAR_GROUP,
  },
  {
    id: "dollar-sofr-iorb",
    topNav: "signals",
    title: "SOFR-IORB 利差",
    component: DollarSofrIorb,
    group: DOLLAR_GROUP,
  },
  {
    id: "dollar-srf",
    topNav: "signals",
    title: "常备回购便利 SRF",
    component: DollarSrf,
    group: DOLLAR_GROUP,
  },
  {
    id: "dollar-bank-cash-buffer",
    topNav: "signals",
    title: "银行现金缓冲",
    component: DollarBankCashBuffer,
    group: DOLLAR_GROUP,
  },
  {
    id: "stablecoin",
    topNav: "signals",
    title: "稳定币",
    component: Stablecoins,
    group: CRYPTO_GROUP,
  },
  {
    id: "funding-rate",
    topNav: "signals",
    title: "资金费率",
    component: FundingRates,
    group: CRYPTO_GROUP,
  },
  {
    id: "fear-greed",
    topNav: "signals",
    title: "贪恐指数",
    component: FearGreed,
    group: CRYPTO_GROUP,
  },
  {
    id: "btc-spot-prices",
    topNav: "signals",
    title: "BTC跨所价差",
    component: BtcSpotPrices,
    group: CRYPTO_GROUP,
  },
  {
    id: "spot-etfs",
    topNav: "signals",
    title: "现货ETFs",
    group: CRYPTO_GROUP,
    externalUrl: "https://www.coinglass.com/zh/etf/bitcoin",
  },
  {
    id: "btc-spot-prices-btc",
    topNav: "signals",
    title: "BTC跨所价差",
    component: BtcSpotPrices,
    group: BTC_GROUP,
  },
  {
    id: "sosovalue-dashboard",
    topNav: "signals",
    title: "sosoValue看板",
    group: BTC_GROUP,
    externalUrl: "https://sosovalue.com/zh/dashboard/bitcoin",
  },
  {
    id: "liquidation-heatmap",
    topNav: "signals",
    title: "合约清算热力图",
    group: BTC_GROUP,
    externalUrl: "https://www.coinglass.com/zh/pro/futures/LiquidationHeatMap",
  },
  {
    id: "btc-global-assets",
    topNav: "signals",
    title: "BTC全球资产排名",
    group: BTC_GROUP,
    externalUrl: "https://www.coinglass.com/zh/global-assets",
  },
  {
    id: "btc-long-short-ratio",
    topNav: "signals",
    title: "BTC(合约)多空比",
    group: BTC_GROUP,
    externalUrl: "https://www.coinglass.com/zh/LongShortRatio",
  },
  {
    id: "btc-power-trend",
    topNav: "signals",
    title: "BTC幂律趋势",
    component: BtcPowerTrend,
    group: BTC_GROUP,
  },
  {
    id: "btc-power-law-forecast",
    topNav: "signals",
    title: "BTC幂律预测",
    component: BtcPowerLaw,
    group: BTC_GROUP,
  },

  // 实时盘口(原 Feed tab)— 依赖 /api/feed + /ws,对 user 角色隐藏。
  {
    id: "orderbook",
    topNav: "signals",
    title: "BN Spot",
    component: OrderbookBinance,
    group: ORDERBOOK_GROUP,
    requiresWs: true,
  },
  {
    id: "bn-perp-orderbook",
    topNav: "signals",
    title: "BN Perp",
    component: OrderbookBinancePerp,
    group: ORDERBOOK_GROUP,
    requiresWs: true,
  },
  {
    id: "hl-orderbook",
    topNav: "signals",
    title: "HL Perp",
    component: OrderbookHyperliquid,
    group: ORDERBOOK_GROUP,
    requiresWs: true,
  },
  {
    id: "by-spot-orderbook",
    topNav: "signals",
    title: "Bybit Spot",
    component: OrderbookBybitSpot,
    group: ORDERBOOK_GROUP,
    requiresWs: true,
  },
  {
    id: "by-perp-orderbook",
    topNav: "signals",
    title: "Bybit Perp",
    component: OrderbookBybitPerp,
    group: ORDERBOOK_GROUP,
    requiresWs: true,
  },
  {
    id: "bg-spot-orderbook",
    topNav: "signals",
    title: "Bitget Spot",
    component: OrderbookBitgetSpot,
    group: ORDERBOOK_GROUP,
    requiresWs: true,
  },
  {
    id: "bg-perp-orderbook",
    topNav: "signals",
    title: "Bitget Perp",
    component: OrderbookBitgetPerp,
    group: ORDERBOOK_GROUP,
    requiresWs: true,
  },
  {
    id: "gt-spot-orderbook",
    topNav: "signals",
    title: "Gateio Spot",
    component: OrderbookGateioSpot,
    group: ORDERBOOK_GROUP,
    requiresWs: true,
  },
  {
    id: "gt-perp-orderbook",
    topNav: "signals",
    title: "Gateio Perp",
    component: OrderbookGateioPerp,
    group: ORDERBOOK_GROUP,
    requiresWs: true,
  },
  {
    id: "okx-spot-orderbook",
    topNav: "signals",
    title: "OKX Spot",
    component: OrderbookOkxSpot,
    group: ORDERBOOK_GROUP,
    requiresWs: true,
  },
  {
    id: "okx-swap-orderbook",
    topNav: "signals",
    title: "OKX Perp",
    component: OrderbookOkxSwap,
    group: ORDERBOOK_GROUP,
    requiresWs: true,
  },
  // Radar — 统一的信息源雷达(Tweet / HN / Github / Reddit 合并为一个 tab,
  // 数据经 /api/openfish 网关,admin-only)。单 panel:左列媒体两级导航 →
  // 中列内容 → 右列详情,侧栏自动隐藏。新增信息源见 panels/radar/registry.ts。
  { id: "monitor", topNav: "radar", title: "Radar", component: RadarDashboard },

  // 全球投资 — 十大投资工具
  ...INVEST_PANELS,

  // 分形演化论 — 市场分形演化理论的学习地图(纯前端静态内容 + 学习进度
  // localStorage;AI 对话仅界面演示)。单 panel:两列(模块导航 → 内容),
  // 侧栏自动隐藏。
  { id: "learn", topNav: "fractal", title: "分形演化论", component: FractalDashboard },
];

export function sidebarFor(topNav: TopNavId): PanelDef[] {
  return PANELS.filter((p) => p.topNav === topNav);
}

export function findPanel(panelId: string): PanelDef | undefined {
  const [topNav, id] = panelId.split(".", 2) as [TopNavId, string];
  return PANELS.find((p) => p.topNav === topNav && p.id === id);
}
